/**
 * @fileoverview Bootstrap coordination hook that orchestrates the two-phase bootstrap process.
 *
 * This is the main application initialization hook that coordinates fetching of
 * session-critical data and reference/cache data during application startup.
 *
 * ## Architecture Overview
 *
 * The bootstrap coordinator orchestrates two distinct phases:
 *
 * 1. **Critical Phase** (Blocking)
 *    - Fetches session-critical data from `/api/bootstrap/critical`
 *    - Blocks initial UI render until complete
 *    - Contains: campaign, NPCs, maps, combat, plots, lore, images, settings
 *    - Errors in this phase are FATAL and prevent the app from functioning
 *
 * 2. **Cache Phase** (Non-blocking)
 *    - Fetches reference data from `/api/bootstrap/cache`
 *    - Runs in the background after critical phase completes
 *    - Contains: items, spells, treasures, moons, weather conditions
 *    - Errors in this phase are NON-FATAL (logged as warnings)
 *
 * ## Integration with Bootstrap Context
 *
 * This hook must be used within a `<BootstrapProvider>` component tree. It uses:
 *
 * - `useBootstrapContext()` - For shared AbortController and state management
 * - `useAuth()` - To determine if the user is authenticated
 * - `useCriticalBootstrap()` - For Phase 1 critical data fetching
 * - `useCacheBootstrap()` - For Phase 2 cache data fetching
 * - `useBootstrapError()` - For error aggregation and classification
 *
 * ## Return Type
 *
 * Returns an `AppInitializerState` object:
 *
 * ```typescript
 * {
 *   isLoading: boolean;        // True during critical bootstrap
 *   isCacheLoading: boolean;   // True during cache bootstrap
 *   isError: boolean;          // True only for fatal critical errors
 *   error: Error | null;       // The actual critical error
 * }
 * ```
 *
 * ## Usage
 *
 * ```tsx
 * function App() {
 *   return (
 *     <BootstrapProvider>
 *       <AppContent />
 *     </BootstrapProvider>
 *   );
 * }
 *
 * function AppContent() {
 *   const { isLoading, isCacheLoading, isError, error } = useBootstrapCoordinator();
 *
 *   if (isLoading) return <LoadingSpinner />;
 *   if (isError) return <ErrorScreen error={error} />;
 *
 *   return <MainApp />;
 * }
 * ```
 *
 * @see react-ui/src/hooks/bootstrap/types.ts for AppInitializerState type
 * @see react-ui/src/hooks/bootstrap/BootstrapProvider.tsx for context provider
 * @see react-ui/src/hooks/bootstrap/useCriticalBootstrap.ts for Phase 1 logic
 * @see react-ui/src/hooks/bootstrap/useCacheBootstrap.ts for Phase 2 logic
 * @see react-ui/src/hooks/bootstrap/useBootstrapError.ts for error handling
 * @see docs/adr/0002-bootstrap-state-hydration.md for architecture decisions
 */

import { useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../useAuth';
import { useBootstrapContext } from './BootstrapProvider';
import { useCriticalBootstrap } from './useCriticalBootstrap';
import { useCacheBootstrap } from './useCacheBootstrap';
import { useBootstrapError } from './useBootstrapError';
import type { AppInitializerState } from './types';
import { logger } from '../../services/logger.client';

// =============================================================================
// Types
// =============================================================================

/**
 * Result type for the useBootstrapCoordinator hook.
 *
 * Provides loading and error states for both bootstrap phases:
 * - Critical phase (blocking) - isLoading, isError, error
 * - Cache phase (non-blocking) - isCacheLoading
 */
export type BootstrapCoordinatorResult = AppInitializerState;

// =============================================================================
// Hook Implementation
// =============================================================================

/**
 * Coordinates the two-phase bootstrap process.
 *
 * This is the main application initialization hook. It orchestrates:
 *
 * 1. **Critical Bootstrap** - Fetches session-critical data, blocks UI render
 * 2. **Cache Bootstrap** - Fetches reference data in the background
 *
 * ## Prerequisites
 *
 * This hook MUST be used within a `<BootstrapProvider>` component tree.
 * If used outside of the provider, it will throw an error.
 *
 * ## Orchestration Logic
 *
 * 1. Wait for authentication check to complete
 * 2. If authenticated, create AbortController and start critical bootstrap
 * 3. When critical bootstrap completes successfully, start cache bootstrap
 * 4. Aggregate loading and error states from both phases
 *
 * ## Error Handling
 *
 * - **Critical errors** are FATAL - set `isError: true` and provide `error` object
 * - **Cache errors** are NON-FATAL - logged as warnings, do not block UI
 *
 * ## Return Value
 *
 * Returns an object matching `AppInitializerState`:
 *
 * - `isLoading`: True while critical data is being fetched
 * - `isCacheLoading`: True while cache data is being fetched
 * - `isError`: True if a fatal error occurred during critical fetch
 * - `error`: The error object if a fatal error occurred
 *
 * @returns {BootstrapCoordinatorResult} The bootstrap state matching AppInitializerState
 * @throws {Error} If used outside of BootstrapProvider
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <BootstrapProvider>
 *       <AppContent />
 *     </BootstrapProvider>
 *   );
 * }
 *
 * function AppContent() {
 *   const { isLoading, isCacheLoading, isError, error } = useBootstrapCoordinator();
 *
 *   // Block render during critical loading
 *   if (isLoading) {
 *     return <LoadingSpinner />;
 *   }
 *
 *   // Show error screen for fatal errors
 *   if (isError && error) {
 *     return <ErrorScreen error={error} />;
 *   }
 *
 *   // Render main UI - cache may still be loading
 *   return (
 *     <MainApp>
 *       {isCacheLoading && <CacheLoadingIndicator />}
 *     </MainApp>
 *   );
 * }
 * ```
 *
 * @example
 * ```tsx
 * // With retry support via useBootstrapError
 * function AppContent() {
 *   const { isLoading, isError, error } = useBootstrapCoordinator();
 *   const { retry } = useBootstrapError(error, null);
 *
 *   if (isError && error) {
 *     return (
 *       <div>
 *         <p>Error: {error.message}</p>
 *         <button onClick={retry}>Retry</button>
 *       </div>
 *     );
 *   }
 *
 *   // ...
 * }
 * ```
 */
export function useBootstrapCoordinator(): BootstrapCoordinatorResult {
  // ---------------------------------------------------------------------------
  // Get authentication state
  // ---------------------------------------------------------------------------

  const { isAuthenticated } = useAuth();

  // ---------------------------------------------------------------------------
  // Get bootstrap context
  // ---------------------------------------------------------------------------

  const {
    abortController,
    createAbortController,
  } = useBootstrapContext();

  // ---------------------------------------------------------------------------
  // Refs for tracking initialization
  // ---------------------------------------------------------------------------

  /**
   * Tracks whether we have created an AbortController for this bootstrap session.
   * This prevents creating multiple controllers on re-renders.
   */
  const hasCreatedControllerRef = useRef<boolean>(false);

  /**
   * Tracks whether the component is still mounted.
   * Used to prevent state updates after unmount.
   */
  const isMountedRef = useRef<boolean>(true);

  // ---------------------------------------------------------------------------
  // AbortController management
  // ---------------------------------------------------------------------------

  /**
   * Create AbortController when authenticated and not already created.
   *
   * We use an effect to ensure the controller is created before the bootstrap
   * hooks try to use it. The controller is created once per authentication
   * session and reused across both bootstrap phases.
   */
  useEffect(() => {
    isMountedRef.current = true;

    logger.debug(
      {
        isAuthenticated,
        hasAbortController: abortController !== null,
        hasCreatedController: hasCreatedControllerRef.current,
      },
      'useBootstrapCoordinator: Checking if AbortController needs to be created'
    );

    // Create controller if authenticated and we haven't created one yet
    if (isAuthenticated && !abortController && !hasCreatedControllerRef.current) {
      logger.info(
        'useBootstrapCoordinator: Creating AbortController for bootstrap session'
      );
      createAbortController();
      hasCreatedControllerRef.current = true;
    }

    // Reset the flag when user logs out (isAuthenticated becomes false)
    if (!isAuthenticated) {
      hasCreatedControllerRef.current = false;
    }

    return () => {
      isMountedRef.current = false;
      logger.debug('useBootstrapCoordinator: Component unmounting');
    };
  }, [isAuthenticated, abortController, createAbortController]);

  // ---------------------------------------------------------------------------
  // Get the signal for bootstrap hooks
  // ---------------------------------------------------------------------------

  /**
   * The AbortSignal to pass to bootstrap hooks.
   *
   * If no controller exists yet (before the effect runs), we create a dummy
   * signal that will not be used because bootstrap won't start without
   * authentication and a proper controller.
   */
  const signal = useMemo<AbortSignal>(() => {
    if (abortController) {
      return abortController.signal;
    }
    // Return a dummy signal that will never abort
    // This is safe because bootstrap hooks check isAuthenticated before fetching
    return new AbortController().signal;
  }, [abortController]);

  // ---------------------------------------------------------------------------
  // Phase 1: Critical Bootstrap
  // ---------------------------------------------------------------------------

  const {
    isLoading: isCriticalLoading,
    isComplete: isCriticalComplete,
    error: criticalError,
  } = useCriticalBootstrap(isAuthenticated, signal);

  // ---------------------------------------------------------------------------
  // Phase 2: Cache Bootstrap
  // ---------------------------------------------------------------------------

  const {
    isLoading: isCacheLoading,
    error: cacheError,
  } = useCacheBootstrap({
    criticalComplete: isCriticalComplete,
    signal,
  });

  // ---------------------------------------------------------------------------
  // Error Aggregation
  // ---------------------------------------------------------------------------

  const { isError, error } = useBootstrapError(criticalError, cacheError);

  // ---------------------------------------------------------------------------
  // Debug logging
  // ---------------------------------------------------------------------------

  useEffect(() => {
    logger.debug(
      {
        isAuthenticated,
        isCriticalLoading,
        isCriticalComplete,
        isCacheLoading,
        hasCriticalError: criticalError !== null,
        hasCacheError: cacheError !== null,
        isError,
      },
      'useBootstrapCoordinator: State updated'
    );
  }, [
    isAuthenticated,
    isCriticalLoading,
    isCriticalComplete,
    isCacheLoading,
    criticalError,
    cacheError,
    isError,
  ]);

  // ---------------------------------------------------------------------------
  // Build and return result
  // ---------------------------------------------------------------------------

  /**
   * Memoize the result to prevent unnecessary re-renders in consuming components.
   *
   * The result is only recalculated when the underlying state values change.
   * This follows the same pattern as useMemo in useBootstrapError.
   */
  const result = useMemo<BootstrapCoordinatorResult>(() => {
    // Compute isLoading:
    // - True while critical data is being fetched (blocks initial render)
    // - Also true if authenticated but critical hasn't started/completed yet
    //   (waiting for AbortController creation)
    const isLoading = isAuthenticated
      ? isCriticalLoading || (!isCriticalComplete && !isError)
      : false;

    logger.debug(
      {
        returning: {
          isLoading,
          isCacheLoading,
          isError,
          hasError: error !== null,
        },
      },
      'useBootstrapCoordinator: Returning result'
    );

    return {
      isLoading,
      isCacheLoading,
      isError,
      error,
    };
  }, [isAuthenticated, isCriticalLoading, isCriticalComplete, isCacheLoading, isError, error]);

  return result;
}

// =============================================================================
// Utility: Reset function for testing
// =============================================================================

/**
 * Resets the bootstrap coordinator state for testing.
 *
 * This function resets the internal state of all bootstrap hooks:
 * - Critical bootstrap module-level state
 * - Cache bootstrap module-level state (via component remount)
 *
 * In production, the state is reset automatically when the user logs out
 * (via the BootstrapProvider's abortBootstrap action).
 *
 * @internal This is exported for testing purposes only.
 *
 * @example
 * ```tsx
 * // In test setup
 * import { resetBootstrapCoordinatorState } from './useBootstrapCoordinator';
 *
 * beforeEach(() => {
 *   resetBootstrapCoordinatorState();
 * });
 * ```
 */
export { resetCriticalBootstrapState as resetBootstrapCoordinatorState } from './useCriticalBootstrap';
